import { z } from 'zod'

const STORAGE_PREFIX = 'daisy:dtools-si-import-attempt:v1:'

export const persistedDtoolsSiAttemptSchema = z.object({
  version: z.literal(1),
  quoteId: z.string(),
  siProjectId: z.string(),
  attemptId: z.string(),
  replaceExisting: z.boolean(),
  startedAt: z.number(),
  recoveryDeadlineAt: z.number(),
})

export type PersistedDtoolsSiAttempt = z.infer<typeof persistedDtoolsSiAttemptSchema>

type AttemptStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>

function legacyStorageKey(quoteId: string, siProjectId: string) {
  return `${STORAGE_PREFIX}${encodeURIComponent(quoteId)}:${encodeURIComponent(siProjectId)}`
}

function storageKey(quoteId: string, siProjectId: string, attemptId: string) {
  return `${legacyStorageKey(quoteId, siProjectId)}:${encodeURIComponent(attemptId)}`
}

function browserStorage(): AttemptStorage | null {
  return typeof localStorage === 'undefined' ? null : localStorage
}

export function saveDtoolsSiImportAttempt(
  attempt: PersistedDtoolsSiAttempt,
  storage: AttemptStorage | null = browserStorage()
) {
  if (!storage) return
  storage.setItem(
    storageKey(attempt.quoteId, attempt.siProjectId, attempt.attemptId),
    JSON.stringify(attempt)
  )
}

export function removeDtoolsSiImportAttempt(
  quoteId: string,
  siProjectId: string,
  attemptId: string,
  storage: AttemptStorage | null = browserStorage()
) {
  if (!storage) return
  storage.removeItem(storageKey(quoteId, siProjectId, attemptId))

  const legacyKey = legacyStorageKey(quoteId, siProjectId)
  const legacyValue = storage.getItem(legacyKey)
  if (!legacyValue) return
  try {
    const parsed = persistedDtoolsSiAttemptSchema.safeParse(JSON.parse(legacyValue))
    if (parsed.success && parsed.data.attemptId === attemptId) storage.removeItem(legacyKey)
  } catch {
    // Leave malformed legacy state isolated; listing already ignores it.
  }
}

export function listDtoolsSiImportAttempts(
  quoteId: string,
  storage: AttemptStorage | null = browserStorage()
): PersistedDtoolsSiAttempt[] {
  if (!storage) return []
  const attempts: PersistedDtoolsSiAttempt[] = []
  for (let index = 0; index < storage.length; index++) {
    const key = storage.key(index)
    if (!key?.startsWith(STORAGE_PREFIX)) continue
    const raw = storage.getItem(key)
    if (!raw) continue
    try {
      const parsed = persistedDtoolsSiAttemptSchema.safeParse(JSON.parse(raw))
      if (parsed.success && parsed.data.quoteId === quoteId) attempts.push(parsed.data)
    } catch {
      // Malformed or obsolete entries remain isolated and cannot trigger a retry.
    }
  }
  return attempts.sort((left, right) => left.startedAt - right.startedAt)
}
